import { Theme } from "src/gen/schema/models/auth";
import { getSystemTheme } from "src/utils/theme";

/**
 * Helpers for Cisco Cloud Control (C3) launch flows.
 *
 * We store launch context in sessionStorage because the C3 login flow performs
 * a full-page navigation (window.location.href), which clears in-memory state.
 */

// We persist the env, URLs and the access token.
const C3_ENV_KEY = "c3Env";
const C3_THEME_KEY = "c3Theme";
const C3_BASE_URL_KEY = "c3BaseUrl";
const C3_ACCESS_TOKEN_KEY = "c3AccessToken";
const C3_AI_ASSISTANT_BASE_URL_KEY = "aiAssistantBaseUrl";
const C3_AI_ASSISTANT_ENTRY_URL_KEY = "aiAssistantEntryUrl";

export const THEME_DARK = "dark";
export const THEME_LIGHT = "light";
export const THEME_SYSTEM = "system";
export const C3_TENANT_SWITCH_EVENT = "c3TenantSwitchEvent";

export interface C3TenantSwitchEvent {
  cuiTenantId: string;
}

export interface C3Context {
  env: string;
  theme: string;
  baseUrl: string;
  accessToken: string;
  aiAssistantBaseUrl: string;
  aiAssistantEntryUrl: string;
}

/**
 * Derive the origin from a given URL string.
 * Returns undefined if the input is not a valid URL.
 *
 * @param url
 */
function toOrigin(url: string) {
  try {
    return new URL(url).origin;
  } catch {
    return "";
  }
}

/**
 * Derive the full URL from a given URL string.
 * Returns an empty string if the input is not a valid URL.
 *
 * @param url
 */
function toUrl(url: string) {
  try {
    return new URL(url).toString();
  } catch {
    return "";
  }
}

/**
 * Check if the input theme string is a valid C3 theme.
 * Valid themes are "dark", "light", and "system" (case-insensitive).
 * @param theme
 */
function isValidC3Theme(theme: string) {
  return [THEME_DARK, THEME_LIGHT, THEME_SYSTEM].includes(theme.toLowerCase());
}

/**
 * Read the value of a cookie by name.
 * Returns an empty string if the cookie is not found.
 *
 * @param name
 */
function getCookieValue(name: string) {
  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${name}(?:_dev)?=([^;]*)`, "i")
  );
  return match ? decodeURIComponent(match[1] || "") : "";
}

/**
 * Persist the access token to sessionStorage.
 */
export function persistAccessToken() {
  const token = getCookieValue(C3_ACCESS_TOKEN_KEY);
  if (token) {
    sessionStorage.setItem(C3_ACCESS_TOKEN_KEY, token);
  }
}

/**
 * Persist the baseUrl to sessionStorage.
 * If baseUrl is empty/invalid, nothing is stored.
 */
export function persistBaseUrl() {
  const baseUrlRaw = getCookieValue(C3_BASE_URL_KEY);
  if (baseUrlRaw) {
    const baseUrl = toOrigin(baseUrlRaw);
    if (baseUrl) {
      sessionStorage.setItem(C3_BASE_URL_KEY, baseUrl);
    }
  }
}

/**
 * Persist the AI Assistant baseUrl to sessionStorage.
 * If baseUrl is empty/invalid, nothing is stored.
 */
export function persistAiAssistantBaseUrl() {
  const aiAssistantBaseUrlRaw = getCookieValue(C3_AI_ASSISTANT_BASE_URL_KEY);
  if (aiAssistantBaseUrlRaw) {
    const aiAssistantBaseUrl = toOrigin(aiAssistantBaseUrlRaw);
    if (aiAssistantBaseUrl) {
      sessionStorage.setItem(C3_AI_ASSISTANT_BASE_URL_KEY, aiAssistantBaseUrl);
    }
  }
}

/**
 * Persist the AI Assistant remote entry URL to sessionStorage.
 * If the URL is empty/invalid, nothing is stored.
 */
export function persistAiAssistantEntryUrl() {
  const aiAssistantEntryUrlRaw = getCookieValue(C3_AI_ASSISTANT_ENTRY_URL_KEY);
  if (aiAssistantEntryUrlRaw) {
    const aiAssistantEntryUrl = toUrl(aiAssistantEntryUrlRaw);
    if (aiAssistantEntryUrl) {
      sessionStorage.setItem(
        C3_AI_ASSISTANT_ENTRY_URL_KEY,
        aiAssistantEntryUrl
      );
    }
  }
}

/**
 * Persist the theme to sessionStorage.
 */
export function persistTheme(theme: string) {
  if (isValidC3Theme(theme)) {
    sessionStorage.setItem(C3_THEME_KEY, theme.toLowerCase());
  }
}

/**
 * Persist the env to sessionStorage.
 */
export function persistEnv() {
  const env = getCookieValue(C3_ENV_KEY);
  if (env) {
    sessionStorage.setItem(C3_ENV_KEY, env);
  }
}

/**
 * One-stop persistence for the C3 launch flow. It stores:
 * - `env` read from the `c3Env` cookie
 * - `baseUrl` derived from `document.referrer` (origin only)
 * - `aiAssistantBaseUrl` read from the `aiAssistantBaseUrl` cookie
 * - `aiAssistantEntryUrl` read from the `aiAssistantEntryUrl` cookie
 * - `accessToken` read from the `c3AccessToken` cookie
 */
export function persistC3Context() {
  // Persisting the env
  // necessary for the C3Header to be able to cross launch other C3 apps.
  persistEnv();

  // Persisting the base URL
  // necessary for the C3Header to know where to load the remote MF from
  persistBaseUrl();

  // Persisting the AI Assistant base URL
  // necessary for the C3Header AI Assistant to know which AI Assistant API to use
  persistAiAssistantBaseUrl();

  // Persisting the AI Assistant remote entry URL
  // necessary for the C3Header AI Assistant to know which remote script to load
  persistAiAssistantEntryUrl();

  // Persisting the access token
  // necessary for the C3Header to function properly
  persistAccessToken();
}

/**
 * Read the stored C3 context from sessionStorage (if present).
 */
export function getC3Context(): C3Context {
  // Fetch env.
  const env = sessionStorage.getItem(C3_ENV_KEY) || "";

  // Fetch theme.
  const theme = sessionStorage.getItem(C3_THEME_KEY) || "";

  // Fetch the baseUrl. Normalize again just in case
  // something unexpected was stored.
  const baseUrl = toOrigin(sessionStorage.getItem(C3_BASE_URL_KEY) || "");

  // Fetch the AI Assistant baseUrl. Normalize again just in case
  // something unexpected was stored.
  const aiAssistantBaseUrl = toOrigin(
    sessionStorage.getItem(C3_AI_ASSISTANT_BASE_URL_KEY) || ""
  );

  // Fetch the AI Assistant entryUrl. Normalize again just in case
  // something unexpected was stored.
  const aiAssistantEntryUrl = toUrl(
    sessionStorage.getItem(C3_AI_ASSISTANT_ENTRY_URL_KEY) || ""
  );

  // Fetch access token.
  const accessToken = sessionStorage.getItem(C3_ACCESS_TOKEN_KEY) || "";

  return {
    env,
    theme,
    baseUrl,
    accessToken,
    aiAssistantBaseUrl,
    aiAssistantEntryUrl
  };
}

/**
 * Returns true if the C3 context has the required values to render the C3Header.
 */
export function isValidC3Context(ctx: C3Context): boolean {
  return (
    !!ctx && !!ctx.env && !!ctx.theme && !!ctx.baseUrl && !!ctx.accessToken
  );
}

/**
 * Returns true if the C3 context has the required values to configure AI Assistant.
 */
export function isValidAIAssistantConfig(ctx: C3Context): boolean {
  return (
    !!ctx &&
    !!ctx.accessToken &&
    !!ctx.aiAssistantBaseUrl &&
    !!ctx.aiAssistantEntryUrl
  );
}

/**
 * Derive the local theme to use based on the input theme and a fallback theme.
 * The input theme string can be "dark", "light", or "system". If it's "system",
 * the system theme preference will be used. If the input theme string is not
 * recognized, the fallback theme will be returned.
 *
 * @param inputTheme
 * @param fallbackTheme
 */
export function getC3ToLocalTheme(
  inputTheme: string,
  fallbackTheme: Theme = Theme.LIGHT
) {
  switch (inputTheme.toLowerCase()) {
    case THEME_DARK:
      return Theme.DARK;
    case THEME_LIGHT:
      return Theme.LIGHT;
    case THEME_SYSTEM:
      return getSystemTheme();
    default:
      return fallbackTheme;
  }
}

/**
 * Derive the C3 theme string to use based on the input local theme and a fallback theme.
 * The input theme is a Theme enum value. It will be mapped to "dark" or "light".
 * If the input theme is not recognized, the fallback theme string will be returned.
 * @param inputTheme
 * @param fallbackTheme
 */
export function getLocalToC3Theme(
  inputTheme: Theme,
  fallbackTheme: string = THEME_LIGHT
) {
  switch (inputTheme) {
    case Theme.DARK:
      return THEME_DARK;
    case Theme.LIGHT:
      return THEME_LIGHT;
    default:
      return fallbackTheme;
  }
}

/**
 * Dispatch a custom event to signal that a C3 tenant switch has occurred.
 * The event detail includes the `cuiTenantId` of the new tenant.
 *
 * This event is listened by the TenantPicker MFE added to the C3Header,
 * which will trigger a redirect to C3 with the new tenant context,
 * Switch the user to the new tenant in C3, logout, login, encrypt the access token,
 * and redirect back to the app with the new tenant context.
 * @param cuiTenantId
 */
export function dispatchC3TenantSwitchEvent(cuiTenantId: string) {
  if (cuiTenantId) {
    window.dispatchEvent(
      new CustomEvent<C3TenantSwitchEvent>(C3_TENANT_SWITCH_EVENT, {
        detail: { cuiTenantId }
      })
    );
  }
}
